import { toast } from "@/components/ui/toast";
import { MESSAGES } from "@/components/ui/toast-messages";

export type FeedbackTarget = 'app_ux' | 'search_result' | 'ai_summary' | 'ai_tag' | 'document_content' | 'paragraph_content';

interface FeedbackParams {
  targetType: FeedbackTarget;
  targetId?: string | number | null;
  isPositive?: boolean | null;
  labels?: string[];
  text?: string;
}

/**
 * Sends one piece of feedback, and reports the outcome AFTER the request settles.
 *
 * It used to fire "Thanks for helping us improve!" as its FIRST statement, before the fetch was
 * even issued — so every failure showed the reader a thank-you and then an error underneath it,
 * and a signed-out reader got the thank-you alone while the text went nowhere. POST /api/feedback
 * requires a session and answers 401 without one, which is the common case for this button: the
 * app header offers it on every app page, signed in or not.
 *
 * This is the same fault the bookmark helpers had, for the same reason, and it hid the same class
 * of bug — a write path that had never once succeeded would have looked healthy from the outside.
 */
export async function submitFeedback({
  targetType,
  targetId = null,
  isPositive = null,
  labels = [],
  text = ""
}: FeedbackParams): Promise<{ success: boolean }> {
  try {
    const response = await fetch("/api/feedback", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({
        targetType,
        targetId,
        isPositive: isPositive ?? null,
        labels,
        text
      }),
    });

    if (!response.ok) {
      // Named rather than lumped in with "something went wrong": 401 is not a fault the reader
      // can do anything about unless they are told it is about being signed in.
      if (response.status === 401) {
        toast(MESSAGES.feedback.signInRequired);
        return { success: false };
      }
      const data = await response.json().catch(() => null);
      throw new Error(data?.error ?? `HTTP ${response.status}`);
    }

    toast(MESSAGES.feedback.sent);
    return { success: true };
  } catch (error) {
    console.error("[feedback] submit failed:", error);
    toast(MESSAGES.feedback.failed);
    return { success: false };
  }
}
